import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Star, Shield, Users, CreditCard, AlertTriangle, Scale, Mail } from 'lucide-react';
import { useColorTheme } from '../contexts/ColorThemeContext';
import { cn } from '../util/designUtil';

const TermsCondition: React.FC = () => {
  const { theme } = useColorTheme();

  const SectionIcon = ({ icon: Icon, className }: { icon: any; className?: string }) => (
    <Icon className={cn("h-5 w-5", theme.secondary, className)} />
  );

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-8">
        {/* Title Section */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
            <Shield className="h-4 w-4" />
            <span>Effective Date: June 19, 2025</span>
          </div>
          <p className="mt-4 text-base sm:text-lg text-gray-700 max-w-2xl mx-auto">
            Welcome to Nexbii, a platform that enables fans to connect with influencers, creators, 
            and public figures via one-on-one video calls.
          </p>
        </div>

        {/* Terms Content */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          
          {/* Section 1: Acceptance */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Shield} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">1. Acceptance of Terms</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">By accessing or using Nexbii, you agree to be legally bound by:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>These Terms of Service</li>
                <li>Our Privacy Policy</li>
              </ul>
              <p className="mt-4">If you do not agree, please do not use the platform.</p>
            </div>
          </section>

          {/* Section 2: Eligibility */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">2. Eligibility</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">You must be:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>At least 13 years old, or the minimum age of digital consent in your country</li>
                <li>Able to form a legally binding contract</li>
                <li>Not prohibited from using our services under any applicable law</li>
              </ul>
              <div className={cn("mt-4 p-4 rounded-lg border-l-4", theme.accent)}>
                <p className="font-medium">Important: If you're under 18, you must use Nexbii with a parent or guardian's consent.</p>
              </div>
            </div>
          </section>

          {/* Section 3: Service Overview */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Star} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">3. Service Overview</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Nexbii allows users to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Bid for and schedule one-on-one video calls with influencers</li>
                <li>Connect using Daily.co (third-party video platform)</li>
                <li>Pay via Creem (third-party payment processor)</li>
                <li>Browse verified influencer profiles</li>
              </ul>
              <p className="mt-4">We verify influencers via their public social accounts (Instagram, YouTube, TikTok).</p>
            </div>
          </section>

          {/* Section 4: Account Registration */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">4. Account Registration</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">You must register using:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Social login (e.g. Instagram, TikTok, YouTube), or</li>
                <li>A valid Email Address</li>
              </ul>
              <p className="mt-4 mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and current information</li>
                <li>Keep your login credentials secure</li>
                <li>Notify us of unauthorized access</li>
              </ul>
              <p className="mt-4">Nexbii reserves the right to reject, suspend, or terminate accounts that violate these terms.</p>
            </div>
          </section>

          {/* Section 5: Bidding & Payments */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={CreditCard} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">5. Bidding, Booking & Payments</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <ul className="list-disc list-inside space-y-2">
                <li>Influencers set their per-minute call rates and availability</li>
                <li>Fans place non-reversible bids for available slots</li>
                <li>Once placed, bids cannot be deleted or withdrawn</li>
                <li>Bidding closes one night before the scheduled call</li>
                <li>The highest bidder wins the slot and is charged automatically</li>
              </ul>
              <div className={cn("mt-4 p-4 rounded-lg", theme.accent)}>
                <p className="font-medium">All payments are securely processed by Creem. Nexbii collects a 20% commission from influencer payouts.</p>
              </div>
            </div>
          </section>

          {/* Section 6: Refund Policy */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">6. Refund Policy</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Refunds are only granted in very limited circumstances:</p>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-green-800 mb-2">✅ You MAY be eligible for a refund if:</h4>
                <ul className="list-disc list-inside space-y-1 text-green-700">
                  <li>The influencer fails to appear at the scheduled time</li>
                  <li>You submit valid evidence within 24 hours (e.g. call logs, screenshots)</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-red-800 mb-2">❌ You are NOT eligible for a refund if:</h4>
                <ul className="list-disc list-inside space-y-1 text-red-700">
                  <li>The video call is interrupted or ends early due to either party leaving</li>
                  <li>There is poor internet connection on your or the influencer's side</li>
                  <li>You or the influencer behave rudely or inappropriately</li>
                  <li>You forgot to join the call or were late</li>
                  <li>You bid accidentally or regret the bid later</li>
                  <li>Any other reason, not stated above explicitly</li>
                </ul>
              </div>

              <p className="mt-4 font-medium">Refunds, if granted, are issued solely at Nexbii's discretion as Nexbii Credits.</p>
            </div>
          </section>

          {/* Section 7: User Conduct */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Shield} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">7. User Conduct & Platform Rules</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Harass, abuse, or threaten others</li>
                <li>Use offensive, illegal, or sexual language</li>
                <li>Record or screenshot calls without consent</li>
                <li>Use bots or manipulate the bidding system</li>
                <li>Impersonate someone else or create fake accounts</li>
                <li>Solicit off-platform contact or payments</li>
              </ul>
              
              <p className="mt-6 mb-2">We reserve the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Ban or suspend users</li>
                <li>Apply penalties (temporary or permanent)</li>
                <li>Charge a fine for misconduct (e.g. $50 to unblock your number after abuse)</li>
              </ul>
            </div>
          </section>

          {/* Section 8: Influencer Behavior */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Star} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">8. Influencer Behavior</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Influencers are expected to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Show up on time and deliver agreed call duration</li>
                <li>Avoid inappropriate, misleading, or offensive behavior</li>
                <li>Adhere to platform guidelines</li>
              </ul>
              <p className="mt-4">Nexbii does not guarantee specific influencer performance or availability.</p>
            </div>
          </section>

          {/* Section 9: Call Conduct */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">9. Call Conduct & Technical Issues</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Nexbii uses Daily.co for video calling and is not responsible for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Poor internet connection or dropped calls</li>
                <li>One party leaving early</li>
                <li>Call quality or background environment</li>
                <li>Content or tone of the conversation</li>
              </ul>
              <div className={cn("mt-4 p-4 rounded-lg", theme.accent)}>
                <p className="font-medium">We do not record or store any part of the video call. If either party behaves inappropriately, report the incident through the app.</p>
              </div>
            </div>
          </section>

          {/* Additional Sections */}
          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Scale} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">10. Intellectual Property</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p>All branding, logos, software, and content belong to Nexbii or its licensors. You may not reproduce, distribute, or modify any part of the platform without written permission.</p>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Users} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">11. Third-Party Services</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">By using Nexbii, you also agree to the terms and privacy policies of our service partners:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Creem (payment processing)</li>
                <li>Daily.co (video calls)</li>
                <li>Google, Meta, TikTok, YouTube (for login and account linking)</li>
              </ul>
              <p className="mt-4">We are not responsible for the data collection or behavior of these services. Please review their policies individually.</p>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">12. Legal & Policy Considerations</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the platform if you are under the required age</li>
                <li>Engage in activity that violates your country's laws (e.g. call recording laws)</li>
                <li>Hold Nexbii liable for call content or third-party misuse</li>
                <li>Circumvent platform fees or exploit referrals</li>
              </ul>
              <p className="mt-4">All legal disputes must be resolved under the jurisdiction outlined below.</p>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Scale} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">13. Termination & Suspension</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">We may suspend or terminate your account if:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You breach these terms</li>
                <li>We detect fraud, abuse, or harmful conduct</li>
                <li>You violate our community or legal standards</li>
              </ul>
              <p className="mt-4">You may delete your account anytime by contacting info@nexbii.com</p>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">14. Disclaimers & Limitation of Liability</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">Nexbii is provided "as-is." We do not guarantee:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>That calls will meet your expectations</li>
                <li>Continuous, error-free service</li>
                <li>That influencers will behave a certain way</li>
              </ul>
              <div className={cn("mt-4 p-4 rounded-lg border-l-4", "bg-red-50 border-red-400")}>
                <p className="font-medium text-red-800">To the maximum extent permitted by law:</p>
                <ul className="list-disc list-inside space-y-1 mt-2 text-red-700">
                  <li>We are not liable for indirect, incidental, or consequential damages</li>
                  <li>Our total liability is limited to the amount you paid to us in the past 30 days</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Scale} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">15. Governing Law</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p>These terms are governed by the laws of England and Wales, unless otherwise required by local consumer protection laws. Any disputes shall be resolved in these courts.</p>
            </div>
          </section>

          <section className="p-4 sm:p-6 md:p-8 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={AlertTriangle} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">16. Changes to These Terms</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p>We may update these Terms of Service at any time. We will notify you of significant changes via the app or email. Continued use of Nexbii after changes means you accept the updated terms.</p>
            </div>
          </section>

          {/* Contact Section */}
          <section className="p-4 sm:p-6 md:p-8">
            <div className="flex items-center gap-3 mb-4">
              <SectionIcon icon={Mail} />
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">17. Contact Us</h2>
            </div>
            <div className="prose max-w-none text-gray-700">
              <p className="mb-4">For help, complaints, or data deletion requests:</p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <a href="mailto:info@nexbii.com" className={cn("font-medium", theme.secondary)}>
                    info@nexbii.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-gray-500" />
                  <a href="https://www.nexbii.com" target="_blank" rel="noopener noreferrer" className={cn("font-medium", theme.secondary)}>
                    www.nexbii.com
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-500">
          <p>© 2025 Nexbii. All rights reserved.</p>
          <p className="mt-2">Last updated: June 19, 2025</p>
        </div>
      </main>
    </div>
  );
};

export default TermsCondition;